import { FilterOperator, useGetDisabledQSetsQuery } from '@exam-management/graphql';
import { useNavigate, useParams } from '@exam-management/router';
import type { GridCellParams } from '@shared/components';
import { ORDER_TYPE } from '@shared/graphql';

import { BaseQSetList } from '../../components';
import { useQSetListSearchParams } from '../../hooks';

import columns from './columns';
import DisabledQSetListNoRowsOverlay from './disabled-qset-no-row-overlay';

export default function DisabledQSetList() {
  const { courseId } = useParams('/exam-courses/:courseId/qsets');
  const navigate = useNavigate();
  const [
    { page, pageSize, status, sortOrder },
    { changeSorting, changePageSize, changePageNumber },
  ] = useQSetListSearchParams();

  const { data: disabledQSetsResult, loading: isLoading } = useGetDisabledQSetsQuery({
    variables: {
      searchQSetsInput: {
        paging: {
          pageSize: pageSize,
          currentPage: page,
        },
        order: {
          type: ORDER_TYPE[sortOrder],
          field: 'disabledAt',
        },
        filter: {
          conditions: [
            {
              value: courseId,
              operator: FilterOperator.Equal,
              field: 'examCourseId',
            },
            {
              value: status,
              operator: FilterOperator.Equal,
              field: 'status',
            },
          ],
        },
      },
    },
    fetchPolicy: 'network-only',
  });

  const rows = disabledQSetsResult?.excSearchQSets.qSets ?? [];
  const totalQSets = disabledQSetsResult?.excSearchQSets.total ?? 0;

  const handleCellClick = (cell: GridCellParams) => {
    navigate('/exam-courses/:courseId/qsets/:qSetId', {
      params: { courseId, qSetId: cell.row.id },
    });
  };

  return (
    <BaseQSetList
      loading={isLoading}
      hideFooter={totalQSets === 0}
      rows={rows}
      columns={columns}
      onCellClick={handleCellClick}
      slots={{ noRowsOverlay: DisabledQSetListNoRowsOverlay }}
      slotProps={{
        footer: { total: totalQSets },
        pagination: {
          page: page,
          count: totalQSets,
          rowsPerPage: pageSize,
          onPageChange: (_, page) => {
            changePageNumber(page);
          },
          onRowsPerPageChange: (event) => {
            changePageSize(parseInt(event.target.value));
          },
        },
      }}
      onSortModelChange={(sorting) => {
        const sortModel = sorting[0];

        if (sortModel?.sort && sortModel?.field) {
          return changeSorting({
            sortOrder: sortModel.sort,
            sortBy: sortModel.field,
          });
        }

        return changeSorting({
          sortOrder: null,
          sortBy: null,
        });
      }}
    />
  );
}
